import axios from "axios";
import { dispatchSessionExpired, isAuthEndpoint } from "@/lib/authSession";

const DEFAULT_BACKEND_URL = "http://localhost:8000";
const SENSITIVE_KEY_PATTERN = /token|authorization|password|secret/i;

export const normalizeBackendUrl = (rawUrl = "") => {
  const trimmed = String(rawUrl || "").trim();
  const base = trimmed || DEFAULT_BACKEND_URL;

  const withoutTrailingSlash = base.replace(/\/+$/, "");
  if (withoutTrailingSlash.toLowerCase().endsWith("/api")) {
    return withoutTrailingSlash.slice(0, -4);
  }

  return withoutTrailingSlash;
};

export const BACKEND_BASE_URL = normalizeBackendUrl(process.env.REACT_APP_BACKEND_URL);
export const API_BASE_URL = `${BACKEND_BASE_URL}/api`;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 90000,
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const requestUrl = error?.config?.url || "";

    if (status === 401 && !isAuthEndpoint(requestUrl)) {
      if (apiClient.defaults.headers.common.Authorization) {
        delete apiClient.defaults.headers.common.Authorization;
      }
      dispatchSessionExpired();
    }

    return Promise.reject(error);
  }
);

const sanitizeMetadata = (metadata = {}) =>
  Object.entries(metadata).reduce((accumulator, [key, value]) => {
    if (value === undefined) {
      return accumulator;
    }

    accumulator[key] =
      SENSITIVE_KEY_PATTERN.test(key) && typeof value !== "boolean"
        ? "[redacted]"
        : value;
    return accumulator;
  }, {});

export const translateBackendValidationError = (err) => {
  if (!err) return "";
  if (typeof err === "string") {
    let clean = err
      .replace(/^Value error,\s*/i, "")
      .replace(/^Assertion failed,\s*/i, "");
    if (/rate_changes\.\d+\.value.*Field required/i.test(clean)) {
      return "Renseignez le nouveau montant du TJM.";
    }
    if (/rate_changes\.\d+\.new_value.*Extra inputs/i.test(clean)) {
      return "Certaines informations de l'évolution tarifaire sont incorrectes.";
    }
    if (/pydantic|traceback|extra_forbidden/i.test(clean)) {
      return "Certaines informations de la mission sont incorrectes. Vérifiez les champs signalés avant d'enregistrer.";
    }
    return clean;
  }

  const loc = Array.isArray(err.loc) ? err.loc.filter((x) => x !== "body") : [];
  const field = loc[loc.length - 1] || "";
  const msg = String(err.msg || "");
  const type = String(err.type || "");

  // Rate changes errors
  if (loc.includes("rate_changes")) {
    if (type === "extra_forbidden" || msg.toLowerCase().includes("extra inputs")) {
      return "Certaines informations de l'évolution tarifaire sont incorrectes.";
    }
    if (field === "value" || field === "new_value") {
      if (type === "missing" || msg.toLowerCase().includes("required")) {
        return "Renseignez le nouveau montant du TJM.";
      }
      if (type.includes("greater") || msg.toLowerCase().includes("greater")) {
        return "Le montant saisi doit être un nombre valide supérieur ou égal à zéro, selon les règles de la mission.";
      }
      return "Le montant saisi doit être un nombre valide supérieur ou égal à zéro, selon les règles de la mission.";
    }
    if (field === "effective_date") {
      if (type === "missing" || msg.toLowerCase().includes("required")) {
        return "Sélectionnez la date à partir de laquelle le nouveau tarif doit s'appliquer.";
      }
      return "Cette date est en dehors de la période de la mission. Vérifiez les dates saisies.";
    }
    if (field === "field") {
      return "Le type d'évolution (TJM, CJM ou salaire) est obligatoire.";
    }
    if (msg.includes("doublon") || msg.includes("duplicate")) {
      return "Une évolution de TJM existe déjà à cette date. Modifiez-la ou choisissez une autre date.";
    }
    return "Certaines informations de l'évolution tarifaire sont incorrectes.";
  }

  // Mission fields
  if (field === "tarif_client") {
    if (type === "missing" || msg.toLowerCase().includes("required")) {
      return "Le TJM client est obligatoire.";
    }
    return "Le montant du TJM client doit être un nombre valide supérieur ou égal à zéro.";
  }
  if (field === "tjm_consultant") {
    return "Le coût consultant (CJM) est obligatoire et doit être un nombre valide supérieur ou égal à zéro.";
  }
  if (field === "salaire_annuel") {
    return "Le salaire annuel brut CDI est obligatoire pour une mission CDI.";
  }
  if (field === "start_date") {
    return "La date de début de mission est obligatoire.";
  }
  if (field === "end_date") {
    return "La date de fin doit être postérieure ou égale à la date de début.";
  }
  if (field === "collaborator_id") {
    return "Le collaborateur est obligatoire.";
  }
  if (field === "client_id") {
    return "Le client est obligatoire.";
  }
  if (field === "type") {
    return "Le type de contrat de la mission est obligatoire.";
  }
  if (field === "employer_cost_coefficient") {
    return "Le coefficient de coût employeur doit être un nombre positif (ex: 1,50).";
  }

  // Generic Pydantic messages fallback
  if (type === "missing" || msg.toLowerCase().includes("field required")) {
    return `Le champ "${field || "requis"}" est obligatoire.`;
  }
  if (type === "extra_forbidden" || msg.toLowerCase().includes("extra inputs")) {
    return "Certaines informations transmises ne sont pas autorisées.";
  }

  let cleanMsg = msg
    .replace(/^Value error,\s*/i, "")
    .replace(/^Assertion failed,\s*/i, "");

  if (cleanMsg.includes("Traceback") || cleanMsg.includes("pydantic") || cleanMsg.includes("extra_forbidden")) {
    return "Certaines informations de la mission sont incorrectes. Vérifiez les champs signalés avant d'enregistrer.";
  }

  return cleanMsg || "Certaines informations sont incorrectes.";
};

export const extractFieldValidationErrors = (error) => {
  const detail = error?.response?.data?.detail;
  if (!Array.isArray(detail)) {
    return {};
  }
  const result = {};
  for (const err of detail) {
    if (!err || typeof err !== "object") continue;
    const loc = Array.isArray(err.loc) ? err.loc.filter((x) => x !== "body") : [];
    if (loc.length === 0) continue;
    const key = loc.includes("rate_changes") ? "rate_changes" : loc[loc.length - 1];
    if (!result[key]) {
      result[key] = translateBackendValidationError(err);
    }
  }
  return result;
};

export const normalizeApiError = (error, fallbackMessage) => {
  const status = error?.response?.status ?? null;
  const backendMessage =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    "";
  const isNetworkError =
    !status &&
    (error?.code === "ERR_NETWORK" ||
      error?.message === "Network Error" ||
      error?.name === "AxiosError");

  let parsedBackendMessage = "";
  if (backendMessage) {
    if (Array.isArray(backendMessage)) {
      parsedBackendMessage = Array.from(
        new Set(backendMessage.map(translateBackendValidationError).filter(Boolean)),
      ).join(" ");
    } else if (typeof backendMessage === "object") {
      parsedBackendMessage = backendMessage.message || translateBackendValidationError(backendMessage);
    } else {
      parsedBackendMessage = translateBackendValidationError(String(backendMessage));
    }
  }

  let message = fallbackMessage || "Une erreur est survenue. Veuillez réessayer.";

  if (status === 401) {
    message = "Session expirée, veuillez vous reconnecter";
  } else if (status === 403) {
    message = "Accès refusé";
  } else if (parsedBackendMessage) {
    message = parsedBackendMessage;
  } else if (isNetworkError) {
    message = "Service indisponible, réessayez";
  } else if (status && status >= 500) {
    message = fallbackMessage || "Service indisponible, réessayez";
  } else if (status === 404) {
    message = fallbackMessage || "Ressource introuvable.";
  } else if (status === 409) {
    message = fallbackMessage || "Conflit : l'action ne peut pas être effectuée dans l'état actuel.";
  }

  return {
    status,
    code: error?.code || null,
    isNetworkError,
    backendMessage,
    message,
  };
};

export const isRetryableApiError = (error) => {
  const normalized = normalizeApiError(error);
  return normalized.isNetworkError || (normalized.status !== null && normalized.status >= 500);
};

export const logAppError = (scope, error, metadata = {}) => {
  const normalized = normalizeApiError(error);
  const structuredLog = {
    scope,
    status: normalized.status,
    code: normalized.code,
    isNetworkError: normalized.isNetworkError,
    message: normalized.message,
    url: error?.config?.url || null,
    method: error?.config?.method || null,
    metadata: sanitizeMetadata(metadata),
  };

  console.error("[lenvia-ui-error]", structuredLog);
};

export const setApiClientAuthToken = (token) => {
  if (token) {
    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
    return;
  }

  delete apiClient.defaults.headers.common.Authorization;
};

export const getApiErrorMessage = (
  error,
  fallbackMessage = "Une erreur est survenue. Veuillez réessayer.",
) => {
  return normalizeApiError(error, fallbackMessage).message;
};

export default apiClient;
